/**
 * Dialog for loading desktops from WebCharts Antigo (LegacyStorage) and, behind
 * the `silverlightImport` feature flag, importing desktops from the Silverlight
 * legacy app (SilverlightStorage → sldZip decode → silverlightConverter).
 */

import React, { useState, useEffect } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tooltip } from "primereact/tooltip";
import { TabView, TabPanel } from "primereact/tabview";
import PropTypes from "prop-types";
import ModalBase from "../../components/Modals/ModalBase";
import { isFeatureEnabled } from "../../data/Services/settings";
import "./DesktopManager.css";

const LoadLegacyDesktopDialog = ({
  visible,
  onHide,
  onImport,
  onImportSilverlight,
}) => {
  const [desktops, setDesktops] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState(0);

  // Silverlight tab state (only used when the flag is on)
  const [silverlightDesktops, setSilverlightDesktops] = useState([]);
  const [silverlightLoading, setSilverlightLoading] = useState(false);
  const [silverlightError, setSilverlightError] = useState("");

  // Flag lida a CADA render (USE time) — nunca em escopo de módulo
  const silverlightEnabled = isFeatureEnabled("silverlightImport");

  // Load legacy desktops when dialog opens
  useEffect(() => {
    if (visible) {
      loadLegacyDesktops();
      if (isFeatureEnabled("silverlightImport")) {
        loadSilverlightDesktops();
      }
    }
  }, [visible]);

  const getInitializedStorageService = async () => {
    // Import services dynamically to avoid circular dependencies
    const { default: storageService } = await import(
      "~/data/Services/storageService"
    );
    const { default: marketdata } = await import("~/data/Services/marketdata");

    // Get a valid token (will refresh if expired)
    const token = await marketdata.getValidToken();

    if (!token) {
      throw new Error(
        "Não foi possível obter token de autenticação. Por favor, faça login novamente.",
      );
    }

    storageService.init(marketdata.getUrl(), token);
    return storageService;
  };

  const loadLegacyDesktops = async () => {
    setLoading(true);
    setError("");

    try {
      const storageService = await getInitializedStorageService();

      console.log("[LoadLegacyDesktopDialog] Loading legacy desktops...");
      const list = await storageService.getLegacyDesktopList();

      console.log(
        "[LoadLegacyDesktopDialog] Got legacy desktops:",
        list?.length || 0,
      );
      setDesktops(list || []);
    } catch (err) {
      console.error(
        "[LoadLegacyDesktopDialog] Error loading legacy desktops:",
        err,
      );
      setError(
        err.message ||
          "Falha ao carregar lista de desktops do WebCharts Antigo",
      );
      setDesktops([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSilverlightDesktops = async () => {
    setSilverlightLoading(true);
    setSilverlightError("");

    try {
      const storageService = await getInitializedStorageService();

      console.log("[LoadLegacyDesktopDialog] Loading silverlight desktops...");
      const list = await storageService.getSilverlightDesktopList();

      console.log(
        "[LoadLegacyDesktopDialog] Got silverlight desktops:",
        list?.length || 0,
      );
      setSilverlightDesktops(list || []);
    } catch (err) {
      console.error(
        "[LoadLegacyDesktopDialog] Error loading silverlight desktops:",
        err,
      );
      setSilverlightError(
        err.message || "Falha ao carregar lista de desktops do Silverlight",
      );
      setSilverlightDesktops([]);
    } finally {
      setSilverlightLoading(false);
    }
  };

  const reloadLists = () => {
    loadLegacyDesktops();
    if (isFeatureEnabled("silverlightImport")) {
      loadSilverlightDesktops();
    }
  };

  const handleImport = async (desktop) => {
    try {
      console.log(
        "[LoadLegacyDesktopDialog] Importing legacy desktop:",
        desktop.chartid,
        desktop.name,
      );
      await onImport(desktop.chartid, desktop.name);
      onHide(); // Close dialog after successful import
    } catch (err) {
      console.error("[LoadLegacyDesktopDialog] Error importing desktop:", err);
      setError(err.message || "Falha ao importar desktop");
    }
  };

  const handleSilverlightImport = async (desktop) => {
    try {
      console.log(
        "[LoadLegacyDesktopDialog] Importing silverlight desktop:",
        desktop.name,
      );
      // PK do storage Silverlight é (login, name) — o name É o identificador
      await onImportSilverlight(desktop.name);
      onHide(); // Close dialog after successful import
    } catch (err) {
      console.error(
        "[LoadLegacyDesktopDialog] Error importing silverlight desktop:",
        err,
      );
      setSilverlightError(err.message || "Falha ao importar desktop");
    }
  };

  const formatDate = (timestamp) => {
    try {
      // Check if timestamp is Unix timestamp (seconds since epoch)
      let date;
      if (typeof timestamp === "number" && timestamp < 4102444800000) {
        // Convert from seconds to milliseconds
        date = new Date(timestamp * 1000);
      } else {
        date = new Date(timestamp);
      }

      return date.toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return timestamp;
    }
  };

  // Column templates
  const nameTemplate = (rowData) => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.375rem",
          cursor: "pointer",
        }}
        onDoubleClick={() => handleImport(rowData)}
      >
        <i
          className="pi pi-file-import"
          style={{ fontSize: "12px", color: "var(--orange-500)" }}
        ></i>
        <span
          style={{
            fontSize: "12px",
            fontWeight: "500",
            color: "var(--text-color)",
          }}
        >
          {rowData.name}
        </span>
      </div>
    );
  };

  const dateTemplate = (rowData) => {
    return (
      <span
        style={{
          fontSize: "11px",
          color: "var(--text-color-secondary)",
        }}
      >
        {formatDate(rowData.timestamp)}
      </span>
    );
  };

  const actionsTemplate = (rowData) => {
    return (
      <div style={{ display: "flex", gap: "1px", justifyContent: "center" }}>
        <Button
          icon="pi pi-download"
          label="Importar"
          className="p-button-sm p-button-success legacy-desktop-action-btn-import"
          onClick={() => handleImport(rowData)}
          data-pr-tooltip="Importar desktop do WebCharts Antigo"
          data-pr-position="top"
          style={{ fontSize: "11px" }}
          data-evo-id="legacydesktopdialog-btn-import"
        />
      </div>
    );
  };

  // Silverlight column templates — sem data (o state legado não guarda
  // timestamp)
  const silverlightNameTemplate = (rowData) => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.375rem",
          cursor: "pointer",
        }}
        onDoubleClick={() => handleSilverlightImport(rowData)}
        data-evo-id={`legacydesktopdialog-silverlight-row-${rowData.name}`}
      >
        <i
          className="pi pi-desktop"
          style={{ fontSize: "12px", color: "var(--orange-500)" }}
        ></i>
        <span
          style={{
            fontSize: "12px",
            fontWeight: "500",
            color: "var(--text-color)",
          }}
        >
          {rowData.name || "default"}
        </span>
        {rowData.isDefault && (
          <span
            style={{
              fontSize: "10px",
              color: "var(--text-color-secondary)",
            }}
          >
            (padrão)
          </span>
        )}
      </div>
    );
  };

  const silverlightObsTemplate = (rowData) => {
    return (
      <span style={{ fontSize: "11px", color: "var(--text-color-secondary)" }}>
        {rowData.obs || ""}
      </span>
    );
  };

  const silverlightActionsTemplate = (rowData) => {
    return (
      <div style={{ display: "flex", gap: "1px", justifyContent: "center" }}>
        <Button
          icon="pi pi-download"
          label="Importar"
          className="p-button-sm p-button-success silverlight-desktop-action-btn-import"
          onClick={() => handleSilverlightImport(rowData)}
          data-pr-tooltip="Importar e converter desktop do Silverlight"
          data-pr-position="top"
          style={{ fontSize: "11px" }}
          data-evo-id="legacydesktopdialog-btn-import-silverlight"
        />
      </div>
    );
  };

  const headerWithSearch = (
    <div className="desktop-dialog-header-inline">
      <i
        className="pi pi-history"
        style={{ color: "var(--orange-500)", fontSize: "0.875rem" }}
      ></i>
      <span className="desktop-dialog-title">
        {silverlightEnabled
          ? "Importar desktops antigos"
          : "Importar do WebCharts Antigo"}
      </span>
      <div className="desktop-header-search">
        <i className="pi pi-search" />
        <input
          type="text"
          autoComplete="off"
          className="p-inputtext p-component"
          placeholder="Buscar..."
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          data-evo-id="legacydesktopdialog-input-filter"
        />
      </div>
      <Button
        icon="pi pi-refresh"
        className="p-button-text p-button-sm p-button-rounded legacy-desktop-refresh-btn"
        onClick={reloadLists}
        disabled={loading || silverlightLoading}
        data-pr-tooltip="Atualizar lista"
        data-pr-position="top"
        style={{ width: "26px", height: "26px", padding: "0" }}
        data-evo-id="legacydesktopdialog-btn-refresh"
      />
    </div>
  );

  const emptyMessage = (
    <div className="desktop-empty-state">
      <i className="pi pi-inbox"></i>
      <p>
        {globalFilter
          ? "Nenhum desktop encontrado com esse nome"
          : "Nenhum desktop do WebCharts Antigo encontrado"}
      </p>
      <p className="desktop-empty-hint">
        Os desktops salvos no WebCharts Antigo aparecerão aqui
      </p>
    </div>
  );

  const silverlightEmptyMessage = (
    <div className="desktop-empty-state">
      <i className="pi pi-inbox"></i>
      <p>
        {globalFilter
          ? "Nenhum desktop encontrado com esse nome"
          : "Nenhum desktop do Silverlight encontrado"}
      </p>
      <p className="desktop-empty-hint">
        Os desktops salvos no aplicativo Silverlight aparecerão aqui
      </p>
    </div>
  );

  const legacyTabContent = (
    <>
      {/* Info Message */}
      <Message
        severity="info"
        text="Selecione um desktop do WebCharts Antigo para importar. O desktop será convertido para o formato atual."
        className="desktop-info-message"
      />

      {/* Error Message */}
      {error && (
        <Message
          severity="error"
          text={error}
          className="desktop-error-message"
        />
      )}

      {/* Desktop Table */}
      {loading ? (
        <div className="desktop-loading-state">
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
          <p>Carregando desktops do WebCharts Antigo...</p>
        </div>
      ) : (
        <DataTable
          value={desktops}
          globalFilter={globalFilter}
          emptyMessage={emptyMessage}
          scrollable
          showGridlines
          stripedRows
          size="small"
          className="desktop-table"
        >
          <Column
            field="name"
            header="Nome"
            body={nameTemplate}
            sortable
            headerClassName="desktop-col-name"
            bodyClassName="desktop-col-name"
          />
          <Column
            field="timestamp"
            header="Data"
            body={dateTemplate}
            sortable
            style={{ width: "130px" }}
            headerClassName="desktop-col-date"
            bodyClassName="desktop-col-date"
          />
          <Column
            header="Ações"
            body={actionsTemplate}
            style={{ width: "120px", textAlign: "center" }}
            headerClassName="desktop-col-actions"
            bodyClassName="desktop-col-actions"
          />
        </DataTable>
      )}
    </>
  );

  const silverlightTabContent = (
    <>
      <Message
        severity="info"
        text="Selecione um desktop do aplicativo Silverlight para importar. O desktop será convertido para o formato atual (indicadores, desenhos e layout)."
        className="desktop-info-message"
      />

      {silverlightError && (
        <Message
          severity="error"
          text={silverlightError}
          className="desktop-error-message"
        />
      )}

      {silverlightLoading ? (
        <div className="desktop-loading-state">
          <ProgressSpinner style={{ width: "50px", height: "50px" }} />
          <p>Carregando desktops do Silverlight...</p>
        </div>
      ) : (
        <DataTable
          value={silverlightDesktops}
          globalFilter={globalFilter}
          emptyMessage={silverlightEmptyMessage}
          scrollable
          showGridlines
          stripedRows
          size="small"
          className="desktop-table"
        >
          <Column
            field="name"
            header="Nome"
            body={silverlightNameTemplate}
            sortable
            headerClassName="desktop-col-name"
            bodyClassName="desktop-col-name"
          />
          <Column
            field="obs"
            header="Obs"
            body={silverlightObsTemplate}
            sortable
            style={{ width: "150px" }}
          />
          <Column
            header="Ações"
            body={silverlightActionsTemplate}
            style={{ width: "120px", textAlign: "center" }}
            headerClassName="desktop-col-actions"
            bodyClassName="desktop-col-actions"
          />
        </DataTable>
      )}
    </>
  );

  return (
    <>
      <Tooltip target=".legacy-desktop-action-btn-import" />
      <Tooltip target=".legacy-desktop-refresh-btn" />
      <Tooltip target=".silverlight-desktop-action-btn-import" />

      <ModalBase
        header={headerWithSearch}
        visible={visible}
        onHide={onHide}
        size="large"
        variant="data"
        footer={null}
        className="desktop-load-dialog-modal legacy-import-modal"
      >
        <div className="desktop-load-dialog">
          {silverlightEnabled ? (
            <TabView
              activeIndex={activeTab}
              onTabChange={(e) => setActiveTab(e.index)}
              className="legacy-import-tabview"
            >
              <TabPanel header="Legado" leftIcon="pi pi-history">
                {legacyTabContent}
              </TabPanel>
              <TabPanel header="Silverlight" leftIcon="pi pi-desktop">
                {silverlightTabContent}
              </TabPanel>
            </TabView>
          ) : (
            legacyTabContent
          )}
        </div>
      </ModalBase>
    </>
  );
};

LoadLegacyDesktopDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onImport: PropTypes.func.isRequired,
  onImportSilverlight: PropTypes.func,
};

export default LoadLegacyDesktopDialog;
